// §11.2 — one terminal tab: its scrollback, its line editor, its history, its cwd.
//
// Pure and synchronous apart from `submit`, which is what makes it testable without a DOM: every
// key produces a new state, and the renderer's only job is to draw the state it is given. The
// alternative — a component that owns a string and a caret in `useState` — is where terminals go
// to acquire off-by-one bugs nobody can reproduce.
//
// **Resolution order** is §11.2's: the built-in provider first, then installed providers in install
// order. §11.2 says "unknown commands resolve against providers in install order", and putting the
// built-in first is what stops an installed app claiming `ls` and quietly changing what the file
// system looks like (D-164).
import type { VfsPath } from '@hangar/shared';
import { commandNotFound, type CommandProvider, type ExecFrame } from './provider';

/** One line in the scrollback. */
export interface TerminalLine {
  readonly id: number;
  readonly kind: 'stdout' | 'stderr' | 'input';
  readonly text: string;
}

export interface TerminalState {
  readonly lines: readonly TerminalLine[];
  /** What is typed but not yet submitted. */
  readonly input: string;
  /** Caret offset within `input`. */
  readonly caret: number;
  readonly cwd: VfsPath;
  /** Newest last. §11.2 gives the terminal history; `Up`/`Down` walk it. */
  readonly history: readonly string[];
  /** Where `Up`/`Down` currently point; `history.length` means "the line being typed". */
  readonly historyIndex: number;
  /** True while a command is producing frames — the prompt is not accepting input. */
  readonly running: boolean;
  readonly exitCode: number | null;
}

/** How much scrollback one tab keeps. A terminal is not a log store. */
export const SCROLLBACK_LIMIT = 5_000;

export function createTerminalState(cwd: VfsPath): TerminalState {
  return {
    lines: [],
    input: '',
    caret: 0,
    cwd,
    history: [],
    historyIndex: 0,
    running: false,
    exitCode: null,
  };
}

let nextLineId = 0;

export function appendLine(
  state: TerminalState,
  kind: TerminalLine['kind'],
  text: string,
): TerminalState {
  nextLineId += 1;
  const lines = [...state.lines, { id: nextLineId, kind, text }];
  return { ...state, lines: lines.slice(-SCROLLBACK_LIMIT) };
}

/**
 * §11.2's line editing.
 *
 * Deliberately the readline subset a person actually uses at a prompt: caret movement, word-wise
 * deletion, home/end, and history. Not a full emacs binding set — every extra chord is a chord an
 * app inside the terminal can never see, and §9.7 already spends the reserved ones.
 */
export function editLine(state: TerminalState, key: string, modifiers: {
  ctrl?: boolean;
  meta?: boolean;
  alt?: boolean;
}): TerminalState | null {
  const { input, caret } = state;

  if (modifiers.ctrl) {
    switch (key) {
      case 'a':
        return { ...state, caret: 0 };
      case 'e':
        return { ...state, caret: input.length };
      case 'u':
        // Kill to the start of the line — the fastest way out of a typo, and muscle memory.
        return { ...state, input: input.slice(caret), caret: 0 };
      case 'k':
        return { ...state, input: input.slice(0, caret) };
      case 'w': {
        const upto = input.slice(0, caret).replace(/\S+\s*$/, '');
        return { ...state, input: upto + input.slice(caret), caret: upto.length };
      }
      case 'c':
        // §11.2 gives no signal to send, and there is nothing server-side to send it to. It
        // abandons the line, which is the half of `^C` that still means something here.
        return { ...appendLine(state, 'input', `${promptFor(state)}${input}^C`), input: '', caret: 0 };
      default:
        return null;
    }
  }
  if (modifiers.meta || modifiers.alt) return null;

  switch (key) {
    case 'ArrowLeft':
      return { ...state, caret: Math.max(0, caret - 1) };
    case 'ArrowRight':
      return { ...state, caret: Math.min(input.length, caret + 1) };
    case 'Home':
      return { ...state, caret: 0 };
    case 'End':
      return { ...state, caret: input.length };
    case 'Backspace':
      if (caret === 0) return state;
      return { ...state, input: input.slice(0, caret - 1) + input.slice(caret), caret: caret - 1 };
    case 'Delete':
      return { ...state, input: input.slice(0, caret) + input.slice(caret + 1) };
    case 'ArrowUp':
      return recallHistory(state, -1);
    case 'ArrowDown':
      return recallHistory(state, 1);
    default:
      if (key.length !== 1) return null;
      return { ...state, input: input.slice(0, caret) + key + input.slice(caret), caret: caret + 1 };
  }
}

function recallHistory(state: TerminalState, delta: number): TerminalState {
  const index = Math.min(state.history.length, Math.max(0, state.historyIndex + delta));
  const recalled = index === state.history.length ? '' : (state.history[index] ?? '');
  return { ...state, historyIndex: index, input: recalled, caret: recalled.length };
}

/** `~/Documents $` — the cwd relative to the user's home, which is what a prompt is for. */
export function promptFor(state: TerminalState, home?: string): string {
  const shown = home && state.cwd.startsWith(home) ? `~${state.cwd.slice(home.length)}` : state.cwd;
  return `${shown} $ `;
}

/**
 * Split a command line into a command and its arguments.
 *
 * Quotes are honoured because a path with a space in it is not exotic, and a terminal that could
 * not open one would be a terminal that could not open half of `Documents`.
 */
export function tokenize(line: string): string[] {
  const tokens: string[] = [];
  const pattern = /"([^"]*)"|'([^']*)'|(\S+)/g;
  for (const match of line.matchAll(pattern)) {
    tokens.push(match[1] ?? match[2] ?? match[3] ?? '');
  }
  return tokens;
}

/**
 * §11.2 — "unknown commands resolve against providers in install order".
 *
 * The built-in is consulted first regardless (D-164): an installed app that claimed `ls` could
 * quietly change what the filesystem appears to contain, and §9.4's trust model does not extend
 * to letting an app redefine the shell's own vocabulary.
 */
export function resolveProvider(
  cmd: string,
  builtin: CommandProvider,
  installed: readonly CommandProvider[],
): CommandProvider | null {
  if (builtin.commands.includes(cmd)) return builtin;
  return installed.find((provider) => provider.commands.includes(cmd)) ?? null;
}

export interface SubmitDeps {
  readonly builtin: CommandProvider;
  readonly providers: () => readonly CommandProvider[];
  /** `cd` and `clear` change the session rather than the world (§11.2). */
  readonly changeDirectory: (cwd: VfsPath, target: string | undefined) => Promise<VfsPath>;
  readonly home: () => string;
}

/**
 * Run one line. Yields a new state after each frame, so the caller can render output **as it
 * arrives** — which is the reason `run` is an `AsyncIterable` in the first place.
 */
export async function* submit(
  state: TerminalState,
  deps: SubmitDeps,
): AsyncGenerator<TerminalState> {
  const line = state.input;
  let next = appendLine(state, 'input', `${promptFor(state, deps.home())}${line}`);
  next = {
    ...next,
    input: '',
    caret: 0,
    running: true,
    // A blank line, or one identical to the last, does not earn a history entry — repeating a
    // command should not make `Up` walk through it twice.
    history:
      line.trim() === '' || next.history.at(-1) === line ? next.history : [...next.history, line],
  };
  next = { ...next, historyIndex: next.history.length };
  yield next;

  const [cmd, ...args] = tokenize(line);
  if (!cmd) {
    yield { ...next, running: false };
    return;
  }

  if (cmd === 'clear') {
    yield { ...next, lines: [], running: false, exitCode: 0 };
    return;
  }

  if (cmd === 'cd') {
    try {
      const cwd = await deps.changeDirectory(next.cwd, args[0]);
      yield { ...next, cwd, running: false, exitCode: 0 };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      yield { ...appendLine(next, 'stderr', `cd: ${message}`), running: false, exitCode: 1 };
    }
    return;
  }

  const provider = resolveProvider(cmd, deps.builtin, deps.providers());
  if (!provider) {
    yield {
      ...appendLine(next, 'stderr', commandNotFound(cmd)),
      running: false,
      exitCode: 127,
    };
    return;
  }

  let current = next;
  try {
    for await (const frame of provider.run(cmd, args, current.cwd)) {
      current = applyFrame(current, frame);
      yield current;
    }
  } catch (error) {
    // A provider that throws mid-stream is an app misbehaving, not the terminal failing (§18 F8's
    // instinct): the line ends, the session does not.
    current = appendLine(current, 'stderr', error instanceof Error ? error.message : String(error));
    current = { ...current, exitCode: 1 };
    yield current;
  }
  yield { ...current, running: false };
}

function applyFrame(state: TerminalState, frame: ExecFrame): TerminalState {
  if (frame.kind === 'exit') return { ...state, exitCode: frame.code ?? 0 };
  const text = frame.text ?? '';
  // A provider may hand over a chunk with several lines in it, or one with a trailing newline;
  // both are normal for a stream, and neither should produce a stray blank row.
  const rows = text.split('\n');
  if (rows.at(-1) === '') rows.pop();
  let next = state;
  for (const row of rows) next = appendLine(next, frame.kind, row);
  return next;
}
